/**
 * Per-user theme preference (dark / light / system) persisted in localStorage.
 * Applies the matching class on `<html>` so the CSS variables in index.css
 * (`:root.light` / `:root.dark`) flip the entire palette.
 *
 * Used by every shell — admin, trader, manager — so the toggle in the
 * Topbar works everywhere.
 */
import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "dark" | "light" | "system";

const STORAGE_KEY = "txprime:theme";

function systemPref(): "dark" | "light" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

function readStored(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === "dark" || v === "light" || v === "system") return v;
  return "dark"; // app default is the premium dark theme
}

function applyClass(mode: ThemeMode) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const resolved = mode === "system" ? systemPref() : mode;
  root.classList.remove("dark", "light");
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => readStored());

  useEffect(() => {
    applyClass(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // localStorage can throw in private mode — harmless to ignore.
    }
  }, [mode]);

  // Follow the OS when the user picks "system".
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const onChange = () => applyClass("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((m) => {
      const resolved = m === "system" ? systemPref() : m;
      return resolved === "dark" ? "light" : "dark";
    });
  }, []);

  const resolved: "dark" | "light" = mode === "system" ? systemPref() : mode;

  return { mode, resolved, setMode, toggle };
}
